import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Plus, CheckCircle2, XCircle } from "lucide-react";
import { useCompany } from "../../context/CompanyContext";
import {
  ensurePayrollPhase2Bootstrap,
  getEmployees,
  getLeaveRequests,
  getLeaveTypes,
  createLeaveType,
  createLeaveRequest,
  updateLeaveRequest,
} from "../payrollService";

const initialLeaveTypeForm = {
  leave_code: "",
  leave_name: "",
  paid_leave: true,
  accrual_method: "annual",
  accrual_days: "",
  max_days_per_year: "",
  carry_forward: false,
  active: true,
};

const initialLeaveRequestForm = {
  employee_id: "",
  leave_type_id: "",
  start_date: "",
  end_date: "",
  days_requested: "",
  reason: "",
};

function pickOne(value) {
  if (!value) return null;
  return Array.isArray(value) ? value[0] || null : value;
}

export default function PayrollLeave() {
  const { companyId } = useCompany();

  const [busy, setBusy] = useState(true);
  const [msg, setMsg] = useState("");

  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);

  const [leaveTypeForm, setLeaveTypeForm] = useState(initialLeaveTypeForm);
  const [leaveRequestForm, setLeaveRequestForm] = useState(
    initialLeaveRequestForm
  );

  const [showLeaveTypeModal, setShowLeaveTypeModal] = useState(false);
  const [showLeaveRequestModal, setShowLeaveRequestModal] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;

    setBusy(true);
    setMsg("");

    try {
      await ensurePayrollPhase2Bootstrap(companyId);

      const [employeeData, typeData, requestData] = await Promise.all([
        getEmployees(companyId),
        getLeaveTypes(companyId),
        getLeaveRequests(companyId),
      ]);

      setEmployees(employeeData || []);
      setLeaveTypes(typeData || []);
      setLeaveRequests(requestData || []);
    } catch (err) {
      setMsg(err.message || "Failed to load leave data");
    } finally {
      setBusy(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const pendingCount = useMemo(
    () => leaveRequests.filter((x) => x.status === "pending").length,
    [leaveRequests]
  );

  async function handleCreateLeaveType(e) {
    e.preventDefault();
    try {
      setMsg("");

      await createLeaveType({
        company_id: companyId,
        leave_code: leaveTypeForm.leave_code.trim().toUpperCase(),
        leave_name: leaveTypeForm.leave_name.trim(),
        paid_leave: !!leaveTypeForm.paid_leave,
        accrual_method: leaveTypeForm.accrual_method,
        accrual_days: Number(leaveTypeForm.accrual_days || 0),
        max_days_per_year: Number(leaveTypeForm.max_days_per_year || 0),
        carry_forward: !!leaveTypeForm.carry_forward,
        active: !!leaveTypeForm.active,
      });

      setLeaveTypeForm(initialLeaveTypeForm);
      setShowLeaveTypeModal(false);
      await load();
      setMsg("Leave type created successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to create leave type");
    }
  }

  async function handleCreateLeaveRequest(e) {
    e.preventDefault();
    try {
      setMsg("");

      await createLeaveRequest({
        company_id: companyId,
        employee_id: leaveRequestForm.employee_id,
        leave_type_id: leaveRequestForm.leave_type_id,
        start_date: leaveRequestForm.start_date,
        end_date: leaveRequestForm.end_date,
        days_requested: Number(leaveRequestForm.days_requested || 0),
        reason: leaveRequestForm.reason,
      });

      setLeaveRequestForm(initialLeaveRequestForm);
      setShowLeaveRequestModal(false);
      await load();
      setMsg("Leave request created successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to create leave request");
    }
  }

  async function handleApprove(row) {
    try {
      setMsg("");
      await updateLeaveRequest(row.id, {
        status: "approved",
        approved_at: new Date().toISOString(),
        rejection_reason: null,
      });
      await load();
      setMsg("Leave request approved.");
    } catch (err) {
      setMsg(err.message || "Failed to approve leave request");
    }
  }

  async function handleReject(row) {
    const reason = window.prompt("Enter rejection reason") || "Rejected";
    try {
      setMsg("");
      await updateLeaveRequest(row.id, {
        status: "rejected",
        rejection_reason: reason,
      });
      await load();
      setMsg("Leave request rejected.");
    } catch (err) {
      setMsg(err.message || "Failed to reject leave request");
    }
  }

  return (
    <div className="payroll-page">
      {msg ? <div className="payroll-alert info">{msg}</div> : null}

      <div className="payroll-grid payroll-grid-4">
        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Leave Types</p>
          <h3>{leaveTypes.length}</h3>
          <span>Configured leave policies</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Leave Requests</p>
          <h3>{leaveRequests.length}</h3>
          <span>Total requests logged</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Pending Requests</p>
          <h3>{pendingCount}</h3>
          <span>Awaiting action</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Paid Leave Types</p>
          <h3>{leaveTypes.filter((x) => x.paid_leave).length}</h3>
          <span>Eligible paid leave categories</span>
        </div>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Leave Management</h3>
            <p>Define leave types and manage employee leave requests.</p>
          </div>

          <div className="table-actions">
            <button
              type="button"
              className="ghost-btn"
              onClick={() => setShowLeaveTypeModal(true)}
            >
              <Plus size={16} />
              Add Leave Type
            </button>

            <button
              type="button"
              className="primary-btn"
              onClick={() => setShowLeaveRequestModal(true)}
            >
              <Plus size={16} />
              New Leave Request
            </button>
          </div>
        </div>

        <div className="payroll-grid payroll-grid-2">
          <div className="payroll-card">
            <div className="payroll-card-header">
              <h3>Leave Types</h3>
            </div>

            <div className="payroll-table-wrap">
              <table className="payroll-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Paid</th>
                    <th>Accrual</th>
                    <th>Max Days</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveTypes.map((row) => (
                    <tr key={row.id}>
                      <td>{row.leave_code}</td>
                      <td>{row.leave_name}</td>
                      <td>{row.paid_leave ? "Yes" : "No"}</td>
                      <td>{row.accrual_method}</td>
                      <td>
                        {Number(row.max_days_per_year || 0).toLocaleString()}
                      </td>
                      <td>
                        <span
                          className={`pill ${
                            row.active ? "active" : "inactive"
                          }`}
                        >
                          {row.active ? "active" : "inactive"}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {!leaveTypes.length && !busy && (
                    <tr>
                      <td colSpan="6">No leave types found.</td>
                    </tr>
                  )}
                  {busy && (
                    <tr>
                      <td colSpan="6">Loading...</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="payroll-card">
            <div className="payroll-card-header">
              <h3>Leave Requests</h3>
            </div>

            <div className="payroll-table-wrap">
              <table className="payroll-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Leave Type</th>
                    <th>Dates</th>
                    <th>Days</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveRequests.map((row) => {
                    const employee = pickOne(row.payroll_employees);
                    const leaveType = pickOne(row.payroll_leave_types);

                    return (
                      <tr key={row.id}>
                        <td>
                          {employee
                            ? `${employee.employee_code} - ${employee.first_name} ${employee.last_name}`
                            : "-"}
                        </td>
                        <td>{leaveType?.leave_name || "-"}</td>
                        <td>
                          {row.start_date} - {row.end_date}
                        </td>
                        <td>
                          {Number(row.days_requested || 0).toLocaleString()}
                        </td>
                        <td>
                          <span className={`pill ${row.status}`}>
                            {row.status}
                          </span>
                        </td>
                        <td>
                          {row.status === "pending" ? (
                            <div className="table-actions">
                              <button
                                type="button"
                                className="icon-btn success"
                                onClick={() => handleApprove(row)}
                                title="Approve"
                              >
                                <CheckCircle2 size={16} />
                              </button>
                              <button
                                type="button"
                                className="icon-btn danger"
                                onClick={() => handleReject(row)}
                                title="Reject"
                              >
                                <XCircle size={16} />
                              </button>
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {!leaveRequests.length && !busy && (
                    <tr>
                      <td colSpan="6">No leave requests found.</td>
                    </tr>
                  )}

                  {busy && (
                    <tr>
                      <td colSpan="6">Loading...</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showLeaveTypeModal ? (
        <div className="payroll-modal-overlay">
          <div className="payroll-modal">
            <div className="payroll-modal-header">
              <h3>Create Leave Type</h3>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setShowLeaveTypeModal(false)}
              >
                Close
              </button>
            </div>

            <form className="payroll-form" onSubmit={handleCreateLeaveType}>
              <div className="payroll-grid payroll-grid-2">
                <label>
                  Leave Code
                  <input
                    value={leaveTypeForm.leave_code}
                    onChange={(e) =>
                      setLeaveTypeForm((p) => ({
                        ...p,
                        leave_code: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  Leave Name
                  <input
                    value={leaveTypeForm.leave_name}
                    onChange={(e) =>
                      setLeaveTypeForm((p) => ({
                        ...p,
                        leave_name: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  Accrual Method
                  <select
                    value={leaveTypeForm.accrual_method}
                    onChange={(e) =>
                      setLeaveTypeForm((p) => ({
                        ...p,
                        accrual_method: e.target.value,
                      }))
                    }
                  >
                    <option value="annual">Annual</option>
                    <option value="monthly">Monthly</option>
                    <option value="manual">Manual</option>
                  </select>
                </label>

                <label>
                  Accrual Days
                  <input
                    type="number"
                    step="0.01"
                    value={leaveTypeForm.accrual_days}
                    onChange={(e) =>
                      setLeaveTypeForm((p) => ({
                        ...p,
                        accrual_days: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Max Days Per Year
                  <input
                    type="number"
                    step="0.01"
                    value={leaveTypeForm.max_days_per_year}
                    onChange={(e) =>
                      setLeaveTypeForm((p) => ({
                        ...p,
                        max_days_per_year: e.target.value,
                      }))
                    }
                  />
                </label>

                <label className="check-inline">
                  <input
                    type="checkbox"
                    checked={leaveTypeForm.paid_leave}
                    onChange={(e) =>
                      setLeaveTypeForm((p) => ({
                        ...p,
                        paid_leave: e.target.checked,
                      }))
                    }
                  />
                  Paid Leave
                </label>

                <label className="check-inline">
                  <input
                    type="checkbox"
                    checked={leaveTypeForm.carry_forward}
                    onChange={(e) =>
                      setLeaveTypeForm((p) => ({
                        ...p,
                        carry_forward: e.target.checked,
                      }))
                    }
                  />
                  Carry Forward
                </label>

                <label className="check-inline">
                  <input
                    type="checkbox"
                    checked={leaveTypeForm.active}
                    onChange={(e) =>
                      setLeaveTypeForm((p) => ({
                        ...p,
                        active: e.target.checked,
                      }))
                    }
                  />
                  Active
                </label>
              </div>

              <div className="payroll-form-actions">
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => setShowLeaveTypeModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-btn">
                  Save Leave Type
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showLeaveRequestModal ? (
        <div className="payroll-modal-overlay">
          <div className="payroll-modal">
            <div className="payroll-modal-header">
              <h3>Create Leave Request</h3>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setShowLeaveRequestModal(false)}
              >
                Close
              </button>
            </div>

            <form className="payroll-form" onSubmit={handleCreateLeaveRequest}>
              <div className="payroll-grid payroll-grid-2">
                <label>
                  Employee
                  <select
                    value={leaveRequestForm.employee_id}
                    onChange={(e) =>
                      setLeaveRequestForm((p) => ({
                        ...p,
                        employee_id: e.target.value,
                      }))
                    }
                    required
                  >
                    <option value="">Select employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.employee_code} - {emp.first_name} {emp.last_name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Leave Type
                  <select
                    value={leaveRequestForm.leave_type_id}
                    onChange={(e) =>
                      setLeaveRequestForm((p) => ({
                        ...p,
                        leave_type_id: e.target.value,
                      }))
                    }
                    required
                  >
                    <option value="">Select leave type</option>
                    {leaveTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.leave_name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Start Date
                  <input
                    type="date"
                    value={leaveRequestForm.start_date}
                    onChange={(e) =>
                      setLeaveRequestForm((p) => ({
                        ...p,
                        start_date: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  End Date
                  <input
                    type="date"
                    value={leaveRequestForm.end_date}
                    onChange={(e) =>
                      setLeaveRequestForm((p) => ({
                        ...p,
                        end_date: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  Days Requested
                  <input
                    type="number"
                    step="0.01"
                    value={leaveRequestForm.days_requested}
                    onChange={(e) =>
                      setLeaveRequestForm((p) => ({
                        ...p,
                        days_requested: e.target.value,
                      }))
                    }
                    required
                  />
                </label>
              </div>

              <label>
                Reason
                <textarea
                  rows="4"
                  value={leaveRequestForm.reason}
                  onChange={(e) =>
                    setLeaveRequestForm((p) => ({
                      ...p,
                      reason: e.target.value,
                    }))
                  }
                />
              </label>

              <div className="payroll-form-actions">
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => setShowLeaveRequestModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-btn">
                  Save Request
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
